import React from 'react'
import { useFormik } from 'formik'
import { useAuthContext } from '../../../context/auth/auth'
import { useTranslation } from 'react-i18next'
import TextButton from '../../Buttons/TextButton'
import useLimitedMode from '../../../services/subscription/useLimitedMode'
import InputWrapper from '../../Formik/InputWrapper'
import Label from '../../Formik/Label'
import Input from '../../Formik/Input'
import useUpdateUser from '../../../services/users/useUpdateUser'
import Spinner from '../../UI/Spinner'
import * as yup from 'yup'

const PersonalInformation = () => {
  const { t } = useTranslation()
  const { isLimited } = useLimitedMode()
  const { authClient } = useAuthContext()
  const [isEdit, setIsEdit] = React.useState(false)
  const { mutate, isLoading } = useUpdateUser()
  // The superadmin settings screen only exposes username and email.
  const isSuperAdmin = authClient?.user_type === 'SUPER_ADMINISTRATOR'

  const validationSchema = yup.object().shape({
    firstName: isSuperAdmin
      ? yup.string()
      : yup.string().required('requiredField'),
    lastName: isSuperAdmin
      ? yup.string()
      : yup.string().required('requiredField'),
    username: yup.string().required('requiredField'),
    email: yup.string().email('invalidEmail').required('requiredField'),
  })

  const formik = useFormik({
    initialValues: {
      firstName: authClient?.first_name ?? '',
      lastName: authClient?.last_name ?? '',
      email: authClient?.email ?? '',
      username: authClient?.username ?? '',
    },
    validationSchema,
    onSubmit: (values) => {
      const data = {
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        username: values.username,
      }
      mutate(data, {
        onSuccess: () => {
          setIsEdit(false)
        },
      })
    },
  })

  const handleEdit = () => {
    if (isEdit) {
      formik.resetForm()
    }
    setIsEdit((prev) => !prev)
  }

  const usernameField = (
    <InputWrapper>
      <Label text={t('username')} />
      {isEdit ? (
        <Input
          id='username'
          as='input'
          type='text'
          hasError={!!(formik.touched.username && formik.errors.username)}
          {...formik.getFieldProps('username')}
        />
      ) : (
        <TextInput text={authClient?.username ?? ''} />
      )}
    </InputWrapper>
  )

  const emailField = (
    <InputWrapper>
      <Label text={t('email')} />
      {isEdit ? (
        <Input
          id='email'
          as='input'
          type='text'
          hasError={!!(formik.touched.email && formik.errors.email)}
          {...formik.getFieldProps('email')}
        />
      ) : (
        <TextInput text={authClient?.email ?? ''} />
      )}
    </InputWrapper>
  )

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className='flex items-center justify-between flex-wrap gap-3'>
        <h2 className='font-bold text-2xl text-[#3D3D3D]'>
          {t('personalInformation')}
        </h2>
        <div className='flex items-center gap-5'>
          {!isEdit && (
            <TextButton
              disabled={isLoading || isLimited}
              title={isLimited ? t('disabledUntilActivation') : undefined}
              label={t('edit')}
              onClickHandler={handleEdit}
            />
          )}
          {isEdit && (
            <>
              <TextButton
                variant='secondary'
                label={t('cancel')}
                disabled={isLoading}
                onClickHandler={handleEdit}
              />
              {isLoading ? (
                <Spinner spinner='form' color='#0C75FF' />
              ) : (
                <TextButton
                  disabled={isLoading}
                  label={t('save')}
                  type='submit'
                />
              )}
            </>
          )}
        </div>
      </div>
      <div className='grid sm:grid-cols-2 gap-5 sm:gap-10 mt-8'>
        {!isSuperAdmin && (
          <>
            <InputWrapper>
              <Label text={t('firstName')} />
              {isEdit ? (
                <Input
                  id='firstName'
                  as='input'
                  type='text'
                  hasError={
                    !!(formik.touched.firstName && formik.errors.firstName)
                  }
                  {...formik.getFieldProps('firstName')}
                />
              ) : (
                <TextInput text={authClient?.first_name ?? ''} />
              )}
            </InputWrapper>
            <InputWrapper>
              <Label text={t('lastName')} />
              {isEdit ? (
                <Input
                  id='lastName'
                  as='input'
                  type='text'
                  hasError={
                    !!(formik.touched.lastName && formik.errors.lastName)
                  }
                  {...formik.getFieldProps('lastName')}
                />
              ) : (
                <TextInput text={authClient?.last_name ?? ''} />
              )}
            </InputWrapper>
          </>
        )}
        {isSuperAdmin ? (
          <>
            {usernameField}
            {emailField}
          </>
        ) : (
          <>
            {emailField}
            {usernameField}
          </>
        )}
      </div>
    </form>
  )
}

export default PersonalInformation

function TextInput({ text }: { text: string }) {
  return <p className='text-lg mt-2 text-lightGray'>{text}</p>
}
